import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const STATUS_COLOR = {
  NOT_CONFIRMED: 'default',
  CONFIRMED: 'success',
  NEEDS_RECONFIRMATION: 'warning',
};

const formatWhen = (value) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '');

/** Where a list of contacts stands; the tooltip says who confirmed it and when. */
export function ReviewTag({ status, by, at, label }) {
  const { t } = useTranslation('teamContacts');
  const detail = [by, formatWhen(at)].filter(Boolean).join(' · ');
  const tag = (
    <Tag color={STATUS_COLOR[status] || 'default'} className="m-0 whitespace-normal">
      {label ? `${label}: ` : ''}
      {t(`review.status.${status}`)}
    </Tag>
  );
  return detail ? <Tooltip title={detail}>{tag}</Tooltip> : tag;
}

const FIELD_LABEL = {
  name: 'contacts.name',
  email: 'contacts.email',
  phone: 'contacts.phone',
  position: 'contacts.position',
  language: 'contacts.language',
  sections: 'contacts.sections',
  inactive: 'contacts.inactive',
  note: 'contacts.note',
};

/** Who changed or confirmed the contacts and when, newest first. */
export function ContactHistory({ entries = [] }) {
  const { t } = useTranslation(['teamContacts', 'teamAssignments']);
  if (!entries.length) return <span className="text-sm opacity-60">{t('history.empty')}</span>;
  return (
    <ul className="m-0 grid list-none gap-2 p-0">
      {entries.map((entry) => (
        <li key={entry._id} className="rounded-lg border border-solid border-black/10 px-3 py-2 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <Tag color={entry.side === 'CLIENT' ? 'blue' : 'default'} className="m-0">
              {t(`history.side.${entry.side}`)}
            </Tag>
            <span className="font-semibold">{entry.actorName || '—'}</span>
            <span className="opacity-60">{formatWhen(entry.at)}</span>
            {entry.kind === 'CONFIRM' && <span>{t('history.confirmed')}</span>}
          </div>
          {(entry.items || []).map((item) => (
            <div key={`${item.action}|${item.contactId}|${item.label}`} className="mt-1 [overflow-wrap:anywhere]">
              <span className="font-medium">{t(`history.action.${item.action}`)}:</span> {item.label || '—'}
              {item.action === 'UPDATE' &&
                item.changes.map((change) => (
                  <div key={change.field} className="ml-3 opacity-70">
                    {t(FIELD_LABEL[change.field] || change.field, { ns: 'teamAssignments' })}: {change.from || '—'} → {change.to || '—'}
                  </div>
                ))}
            </div>
          ))}
        </li>
      ))}
    </ul>
  );
}

/** One contact's sections, a copy with a dashed edge. */
export function SectionTags({ sections = [] }) {
  const { t } = useTranslation(['teamContacts', 'teamAssignments']);
  return (sections || []).map((s) => (
    <Tag key={s.section} className="mb-1" style={{ borderStyle: s.delivery === 'CC' ? 'dashed' : 'solid' }}>
      {t(`sections.${s.section}`)}
      {s.delivery === 'CC' ? ` · ${t('delivery.CC', { ns: 'teamAssignments' })}` : ''}
    </Tag>
  ));
}
